// RPG 창(대장간·상점·교관·장비)의 표시 데이터를 만드는 순수 로직. DOM·three를 모른다.
// 수치와 결과는 GameView.profile/stats/village에서만 온다. 여기서 골드·능력치를 바꾸지 않고, 비교 미리보기만 shared/rpg deriveStats로 계산해 보여 준다.
import { HERO, SKILLS } from '../shared/config';
import type { GrowthMods } from '../shared/growth';
import { SKILL_FOCUS_COOLDOWN, SKILL_INFO, npcDef, townNpcAt, deriveStats, itemById, makeStats, type GearStatsIn } from '../shared/rpg';
import type { ProfileMode, ProfileView, StatsView, TxResultView } from '../shared/types';
import { fmtNum } from './dom';

/** 표시에 필요한 프로필 필드만(테스트에서 GameView 전체를 만들지 않는다) */
export type ProfileLike = Pick<ProfileView, 'mode' | 'gold' | 'pendingGold' | 'owned' | 'weapon' | 'armor' | 'skillFocus'> & {
  /** 단조 개선으로 다른 장비가 된 원본 장비(없으면 빈 것으로 본다). 목록에서 숨긴다 */
  readonly reforged?: readonly string[];
};
export type StatsLike = Pick<StatsView, 'attack' | 'maxHp' | 'maxMp' | 'critChance' | 'skillCooldown'>;

export type StatKey = 'attack' | 'maxHp' | 'maxMp' | 'critChance';
export const STAT_LABEL: Record<StatKey, string> = { attack: '공격력', maxHp: '최대 HP', maxMp: '최대 MP', critChance: '치명률' };
const STAT_KEYS: readonly StatKey[] = ['attack', 'maxHp', 'maxMp', 'critChance'];

export function fmtStat(key: StatKey, v: number): string {
  return key === 'critChance' ? Math.round(v * 1000) / 10 + '%' : fmtNum(v);
}
/** 장비가 더하는 양(+200, +3%) */
export function fmtBonus(key: StatKey, v: number): string {
  return '+' + fmtStat(key, v);
}

/** 거래 가능 여부. 'ok'만 거래·장착·주력 변경을 허용한다 */
export function tradeLocked(mode: ProfileMode): boolean {
  return mode !== 'ok';
}

// ── 능력치 변화(다음 목표 문구 등) ─────────────────
export interface StatDelta { key: StatKey; label: string; from: string; to: string; up: boolean }

// ── 상태 탭 ─────────────────────────────────────
export interface StatRow {
  key: StatKey;
  label: string;
  /** 지금 전투에 쓰이는 값(GameView.stats) */
  total: string;
  base: string;
  /** 장착 장비 6부위 기여분(장비 통합, ECONOMY 14절) */
  gear: string;
  /** 레벨·스킬 트리 기여분. 없으면 +0 */
  growth: string;
  /** 막대 비율용 숫자(기본·장비·성장 기여) */
  parts: [number, number, number];
}
const BASE: Record<StatKey, number> = { attack: HERO.attack, maxHp: HERO.maxHp, maxMp: HERO.maxMp, critChance: HERO.critChance };
const _ng = makeStats();

/**
 * 기본값과 장비·성장(레벨·스킬 트리) 기여 분해. total은 stats에서 그대로 읽는다.
 * 장비 기여 = 합계 − (장비 없이 같은 성장·주력으로 계산한 값), 성장 기여 = 그 값 − 기본. 조각의 합이 항상 실제 능력치와 같다.
 * gear: 장착 장비 합계(GameView.gear 합계, gearTotalsOf). 빼면 장비 몫도 성장 몫에 들어간다
 */
export function statRows(p: ProfileLike, s: StatsLike, gear?: GearStatsIn, mods?: GrowthMods): StatRow[] {
  const noGear = deriveStats(_ng, p.skillFocus, mods);
  return STAT_KEYS.map((key) => {
    const g0 = gear ? s[key] - noGear[key] : 0;
    const gp = Math.abs(g0) < 1e-9 ? 0 : g0;
    const rest = s[key] - BASE[key] - gp;
    const gr = Math.abs(rest) < 1e-9 ? 0 : rest;
    return {
      key,
      label: STAT_LABEL[key],
      total: fmtStat(key, s[key]),
      base: fmtStat(key, BASE[key]),
      gear: fmtBonus(key, gp),
      growth: fmtBonus(key, gr),
      parts: [BASE[key], gp, gr] as [number, number, number],
    };
  });
}

// ── 스킬 ─────────────────────────────────────
export interface SkillRow {
  id: 1 | 2 | 3 | 4 | 5;
  name: string;
  role: string;
  desc: string;
  mp: number;
  /** 지금 재사용 대기(초, GameView.stats 기준) */
  cooldownSec: string;
  /** 주력으로 삼았을 때의 재사용 대기(초, 미리보기) */
  focusCooldownSec: string;
  focused: boolean;
}
const _f = makeStats();
export function fmtSec(ticks: number): string {
  return (Math.round(ticks / 6) / 10).toFixed(1) + '초';
}
export function skillRows(p: ProfileLike, s: StatsLike, mods?: GrowthMods, gear?: GearStatsIn): SkillRow[] {
  // 진홍 초승달(4)·천공 올려베기(5)는 Lv10·Lv5 자동 습득 스킬(강화는 트리 노드)이라, 이 장비 창 표는 기존 세 스킬만 보여 준다(UI 레인이 트리 탭에서 다룬다)
  return SKILL_INFO.filter((info) => info.id <= 3).map((info, i) => {
    deriveStats(_f, info.id, mods, gear);
    return {
      id: info.id as SkillRow["id"], // filter가 1~3만 남긴다(SKILL_INFO.id는 C9부터 1..8)
      name: SKILLS[i].name,
      role: info.role,
      desc: info.desc,
      mp: SKILLS[i].mpCost,
      cooldownSec: fmtSec(s.skillCooldown[i]),
      focusCooldownSec: fmtSec(_f.skillCooldown[i]),
      focused: p.skillFocus === info.id,
    };
  });
}
/** 주력 효과 설명(배율에서 계산: 0.8 → '20%') */
export const FOCUS_BONUS_TEXT = '주력 스킬은 재사용 대기가 ' + Math.round((1 - SKILL_FOCUS_COOLDOWN) * 100) + '% 줄어요.';

// ── 프로필 모드 안내 ───────────────────────────
export interface ModeNotice { text: string; recover: boolean }
export function modeNotice(mode: ProfileMode): ModeNotice | null {
  switch (mode) {
    case 'ok': return null;
    case 'other_tab': return { text: '다른 창에서 거래 중이에요. 그 창을 닫으면 여기서도 이어서 쓸 수 있어요.', recover: false };
    case 'corrupt': return { text: '저장된 장비 기록을 읽지 못해 거래와 장착을 잠갔어요. 새로 시작하면 보유 장비와 구매 기록이 처음 상태로 돌아가요.', recover: true };
    case 'future': return { text: '더 새로운 버전에서 저장한 기록이에요. 이 화면에서는 기록을 건드리지 않고, 거래와 장착을 쉬어요.', recover: false };
    case 'unavailable': return { text: '이 기기에서는 저장 공간을 쓸 수 없어 거래와 장착을 할 수 없어요.', recover: false };
  }
}

// ── 결과 문구 ─────────────────────────────────
export type Tone = 'ok' | 'warn' | 'err';
export interface TxMessage { text: string; tone: Tone }

/** readonly 코드는 모드에 따라 이유를 달리 말한다 */
function readonlyText(mode: ProfileMode): string {
  switch (mode) {
    case 'other_tab': return '다른 창에서 거래 중이라 지금은 할 수 없어요.';
    case 'corrupt': return '저장 기록에 문제가 있어 지금은 거래할 수 없어요.';
    case 'future': return '더 새로운 버전의 저장 기록이라 지금은 거래할 수 없어요.';
    case 'unavailable': return '저장 공간을 쓸 수 없어 거래할 수 없어요.';
    default: return '지금은 거래할 수 없어요.';
  }
}

/** profile.last를 플레이어 문구로 바꾼다. 조용히 넘길 코드(no_menu, duplicate, 결과 없음)는 null */
export function txMessage(last: TxResultView, p: ProfileLike): TxMessage | null {
  if (last.seq === 0 || last.kind === 'none') return null;
  switch (last.code) {
    case 'ok': {
      if (last.kind === 'buy') {
        const it = itemById(last.itemId);
        if (it?.upgradeFrom) return { text: (itemById(it.upgradeFrom)?.name ?? '장비') + '을 ' + it.name + '으로 개선했어요', tone: 'ok' };
        return { text: (it?.name ?? '장비') + ' 구매 완료', tone: 'ok' };
      }
      if (last.kind === 'equip') {
        const it = itemById(last.itemId);
        return { text: it ? it.name + ' 장착 완료' : '갑옷을 벗었어요', tone: 'ok' };
      }
      if (last.kind === 'focus') {
        const f = p.skillFocus;
        return { text: f >= 1 && f <= 3 ? '주력 스킬: ' + SKILLS[f - 1].name : '주력 스킬을 해제했어요', tone: 'ok' };
      }
      if (last.kind === 'recover') return { text: '장비 기록을 새로 시작했어요', tone: 'ok' };
      return null;
    }
    case 'insufficient': return { text: '골드가 부족합니다', tone: 'err' };
    case 'owned': return { text: '이미 가진 장비예요', tone: 'warn' };
    case 'not_owned': return { text: '가지고 있지 않은 장비예요', tone: 'warn' };
    case 'unknown_item': return { text: '알 수 없는 장비예요', tone: 'warn' };
    case 'slot_mismatch': return { text: '이 자리에는 장착할 수 없는 장비예요', tone: 'warn' };
    case 'wrong_npc': return { text: '이 상인은 그 물건을 팔지 않아요', tone: 'warn' };
    case 'paused': return { text: '일시정지 중에는 할 수 없어요', tone: 'warn' };
    case 'readonly': return { text: readonlyText(p.mode), tone: 'warn' };
    case 'save_failed': return { text: '저장하지 못해 거래를 취소했어요. 골드와 장비는 그대로예요.', tone: 'err' };
    case 'needs_base': {
      const base = itemById(itemById(last.itemId)?.upgradeFrom);
      return { text: (base?.name ?? '원본 장비') + '이 있어야 개선할 수 있어요', tone: 'warn' };
    }
    case 'no_menu':
    case 'duplicate':
      return null;
  }
}

/** profile.last.seq가 바뀐 것을 한 번만 알려 준다. 처음 본 값은 이미 지난 결과라 알리지 않는다 */
export class ResultGate {
  private seen = -1;
  /** 새 결과면 true(같은 seq를 다시 주면 false) */
  poll(seq: number): boolean {
    if (this.seen < 0) { this.seen = seq; return false; }
    if (seq === this.seen) return false;
    this.seen = seq;
    return true;
  }
}

// ── 상호작용 안내·NPC 문구 ───────────────────────
export function npcTitle(index: number, town = 1): string {
  const n = townNpcAt(town, index) ?? npcDef(index);
  return n ? n.name + ' · ' + n.title : '';
}
/** "E 대화 · 미라(잡화상)". 창이 열려 있거나 가까운 NPC가 없으면 null. 터치 기기는 키 글자 없이 */
export function interactPrompt(near: number, menuNpc: number, inventoryOpen: boolean, touch: boolean, town = 1): string | null {
  // 그 마을 사람 이름(TOWN-SCENARIO). 그 마을에 없는 인덱스면 기본 정의
  const n = townNpcAt(town, near) ?? npcDef(near);
  if (!n || menuNpc >= 0 || inventoryOpen) return null;
  return (touch ? '대화' : 'E 대화') + ' · ' + n.name + '(' + n.title + ')';
}
/** 보유 골드 문구. 저장 대기 보상은 쓸 수 없는 돈이라 따로 보인다 */
export function goldText(p: Pick<ProfileLike, 'gold' | 'pendingGold'>): string {
  return fmtNum(p.gold) + (p.pendingGold > 0 ? ' · 저장 대기 +' + fmtNum(p.pendingGold) : '');
}

// ── 요청 ID ─────────────────────────────────
let reqCounter = 0;
/** 클릭마다 새 ID. randomUUID가 없는 환경은 카운터 + 시간 + 무작위 */
export function makeRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  reqCounter++;
  return 'r' + reqCounter.toString(36) + '-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);
}


